/**
 * ColumnFilterIcon — icon ▽ gắn vào TableCell header.
 *
 * Click icon → callback nhận viewport position (top/left bên dưới icon).
 * Dùng position thay vì DOM element vì TableVirtuoso thường `key={gridKey}`
 * remount header khi data đổi → anchorEl bị detach → MUI Popover fallback
 * về (0,0) = top-left. Position capture tại click time là **immune** với
 * mọi DOM remount sau đó.
 *
 * `stopPropagation` để click icon không trigger sort của header.
 *
 * Visual:
 *   - active=false: icon outlined, color disabled
 *   - active=true:  icon filled, color primary
 */
import { IconButton } from '@mui/material'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'

export interface ColumnFilterAnchorPosition {
  top: number
  left: number
}

export interface ColumnFilterIconProps {
  active: boolean
  onClick: (anchorPosition: ColumnFilterAnchorPosition) => void
  ariaLabel?: string
}

export function ColumnFilterIcon({ active, onClick, ariaLabel = 'Filter column' }: ColumnFilterIconProps) {
  return (
    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation()
        const rect = e.currentTarget.getBoundingClientRect()
        onClick({ top: rect.bottom, left: rect.left })
        // Blur icon: tránh warning "aria-hidden on focused element" (MUI v5)
        // khi Popover mở áp aria-hidden lên #root trước khi dời focus.
        e.currentTarget.blur()
      }}
      aria-label={ariaLabel}
      sx={{
        p: 0.125,
        ml: 0.25,
        color: active ? 'primary.main' : 'text.disabled',
        '&:hover': { color: 'primary.main' },
      }}
    >
      {active ? (
        <FilterAltIcon sx={{ fontSize: 14 }} />
      ) : (
        <FilterAltOutlinedIcon sx={{ fontSize: 14 }} />
      )}
    </IconButton>
  )
}
